import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../App.js'
import type { WorldSummary } from '../api/client.js'
import { ApiError, api } from '../api/client.js'

export function Home(): ReactNode {
  const { account, loading } = useAuth()
  if (loading) return <main className="empty">Loading…</main>
  return account ? <WorldList /> : <SignIn />
}

function SignIn(): ReactNode {
  const { setAccount } = useAuth()
  const [mode, setMode] = useState<'signIn' | 'signUp'>('signIn')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [inviteCode, setInviteCode] = useState('')
  const [error, setError] = useState<string>()
  const [busy, setBusy] = useState(false)
  const [inviteOnly, setInviteOnly] = useState(true)
  const [waitlisted, setWaitlisted] = useState(false)

  useEffect(() => {
    api
      .authPolicy()
      .then((policy) => setInviteOnly(policy.inviteOnly))
      .catch(() => undefined)
  }, [])

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    setBusy(true)
    setError(undefined)
    try {
      const account =
        mode === 'signIn'
          ? await api.logIn(email, password)
          : await api.signUp({ email, password, displayName, inviteCode: inviteCode || undefined })
      setAccount(account)
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'Something went wrong.')
    } finally {
      setBusy(false)
    }
  }

  const joinWaitlist = async (): Promise<void> => {
    try {
      await api.joinWaitlist(email)
      setWaitlisted(true)
    } catch {
      setError('That address could not be added.')
    }
  }

  return (
    <main style={{ maxWidth: '26rem' }}>
      <h1>Walk through worlds you can only hear</h1>
      <p className="muted">
        Sound placed on real ground, heard in three dimensions through the headphones you already
        own.
      </p>

      <form className="card" onSubmit={submit}>
        <div className="row" style={{ marginBottom: '0.9rem' }}>
          <button
            type="button"
            className={mode === 'signIn' ? 'primary' : 'ghost'}
            onClick={() => setMode('signIn')}
          >
            Sign in
          </button>
          <button
            type="button"
            className={mode === 'signUp' ? 'primary' : 'ghost'}
            onClick={() => setMode('signUp')}
          >
            Create account
          </button>
        </div>

        {mode === 'signUp' ? (
          <div className="field">
            <label htmlFor="name">Your name</label>
            <input id="name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
          </div>
        ) : null}

        <div className="field">
          <label htmlFor="email">Email</label>
          <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>

        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={10}
            required
          />
          {mode === 'signUp' ? <p className="faint">At least ten characters.</p> : null}
        </div>

        {mode === 'signUp' && inviteOnly ? (
          <div className="field">
            <label htmlFor="invite">Invite code</label>
            <input
              id="invite"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              placeholder="ABCD-EFGH"
            />
            <p className="faint">SoundWorld is invite-only for now.</p>
          </div>
        ) : null}

        {error ? <div className="banner bad">{error}</div> : null}

        <button className="primary" type="submit" disabled={busy} style={{ width: '100%' }}>
          {busy ? 'One moment…' : mode === 'signIn' ? 'Sign in' : 'Create account'}
        </button>
      </form>

      {mode === 'signUp' && inviteOnly ? (
        <div className="card">
          <h3>No invite?</h3>
          {waitlisted ? (
            <p className="muted">You are on the list. We will be in touch when signup opens.</p>
          ) : (
            <>
              <p className="muted">Leave your address and we will write when signup opens.</p>
              <button onClick={joinWaitlist} disabled={!email}>
                Join the waitlist
              </button>
            </>
          )}
        </div>
      ) : null}
    </main>
  )
}

function WorldList(): ReactNode {
  const [worlds, setWorlds] = useState<WorldSummary[]>()
  const [error, setError] = useState<string>()
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [invite, setInvite] = useState<string>()
  const navigate = useNavigate()

  const reload = (): void => {
    api
      .listWorlds()
      .then((result) => setWorlds(result.worlds))
      .catch((problem) => setError(problem instanceof ApiError ? problem.message : 'Could not load.'))
  }

  useEffect(reload, [])

  const create = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    setCreating(true)
    try {
      const world = await api.createWorld({ name })
      navigate(`/studio/${world.id}`)
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'Could not create that world.')
    } finally {
      setCreating(false)
    }
  }

  return (
    <main>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h1 style={{ margin: 0 }}>Your worlds</h1>
        <button
          onClick={async () => {
            const created = await api.createInvite()
            setInvite(created.code)
          }}
        >
          Create an invite
        </button>
      </div>

      {invite ? (
        <div className="banner">
          Invite code <strong className="mono">{invite}</strong> — hand it to one person.
        </div>
      ) : null}
      {error ? <div className="banner bad">{error}</div> : null}

      <form className="card" onSubmit={create}>
        <h3>New world</h3>
        <div className="row">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Riverside sculpture walk"
            required
            style={{ flex: 1, minWidth: '14rem' }}
          />
          <button className="primary" type="submit" disabled={creating || !name}>
            Create
          </button>
        </div>
      </form>

      {worlds === undefined ? (
        <p className="muted" style={{ marginTop: '1rem' }}>
          Loading…
        </p>
      ) : worlds.length === 0 ? (
        <div className="empty">Nothing here yet. Make your first world above.</div>
      ) : (
        <div className="grid" style={{ marginTop: '1rem' }}>
          {worlds.map((world) => (
            <div key={world.id} className="card">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <h2 style={{ margin: 0 }}>{world.name}</h2>
                {world.published ? (
                  <span className="pill good">
                    <span className="dot" />
                    published
                  </span>
                ) : (
                  <span className="pill">draft</span>
                )}
              </div>
              <p className="muted">{world.description || 'No description yet.'}</p>
              <div className="row tight faint" style={{ marginBottom: '0.7rem' }}>
                <span className="pill">{world.anchoring}</span>
                <span className="pill">{world.instancing}</span>
                <span className="pill">
                  {world.pricing.free
                    ? 'free'
                    : `${(world.pricing.priceCents / 100).toFixed(2)} ${world.pricing.currency.toUpperCase()}`}
                </span>
              </div>
              <div className="row tight">
                <Link to={`/studio/${world.id}`}>
                  <button>Studio</button>
                </Link>
                <Link to={`/play/${world.id}`}>
                  <button disabled={!world.published}>Listen</button>
                </Link>
                <Link to={`/ops/${world.id}`}>
                  <button className="ghost">Console</button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  )
}
